import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
  Chip,
  IconButton,
  Paper,
  Alert,
  Divider,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { DatePicker, TimePicker } from '@mui/x-date-pickers';
import {
  Add,
  Event,
  CheckCircle,
  LockOpen,
  Cancel,
  Schedule,
  People,
  Warning,
  Edit,
  Delete,
  CheckBox,
  AccessTime,
  LocationOn,
  PersonAdd,
} from '@mui/icons-material';
import axios from 'axios';
import toast from 'react-hot-toast';
import dayjs from 'dayjs';
import { useAuth } from '../contexts/AuthContext';
import { ACCENTS, tint } from '../theme/accents';
import { StatusIndicator } from '../components/Indicators';
import EmptyState from '../components/EmptyState';
import PageHeader from '../components/PageHeader';
import { formatTime, weekdayName } from '../utils/datetime';
import { formatINR } from '../utils/currency';

// Status colours, so a glance tells you which meetings still need running

const MEETING_ACCENT = {
  scheduled: ACCENTS.teal,
  completed: ACCENTS.emerald,
  cancelled: ACCENTS.vermilion,
};

const Meetings = () => {
  const { isAdmin } = useAuth();
  const [meetings, setMeetings] = useState([]);
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [attendanceDialogOpen, setAttendanceDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    meetingDate: dayjs().add(1, 'day'),
    meetingTime: dayjs().hour(22).minute(0),
    location: 'Community Hall',
    notes: '',
  });
  const [attendanceData, setAttendanceData] = useState([]);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    fetchMeetings();
    fetchUsers();
    axios.get('/api/rules/settings').then(r => setSettings(r.data)).catch(() => {});
  }, []);

  const fetchMeetings = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/meetings');
      setMeetings(response.data);
    } catch (error) {
      toast.error('Failed to fetch meetings');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      // The system admin is not a paying member of the group, so it must not be
      // offered anywhere member figures are recorded — see Members/Loans, which
      // already filter this way.
      const response = await axios.get('/api/users');
      setUsers(response.data.filter((user) => !user.is_system_admin));
    } catch (error) {
      console.error('Failed to fetch users');
    }
  };

  const fetchMeetingDetails = async (meetingId) => {
    try {
      const response = await axios.get(`/api/meetings/${meetingId}`);
      setSelectedMeeting(response.data);
      setAttendanceData(response.data.attendance || []);
      setAttendanceDialogOpen(true);
    } catch (error) {
      toast.error('Failed to fetch meeting details');
    }
  };

  const handleOpenDialog = (meeting = null) => {
    if (meeting) {
      setEditMode(true);
      setSelectedMeeting(meeting);
      setFormData({
        meetingDate: dayjs(meeting.meeting_date),
        meetingTime: dayjs(`2024-01-01 ${meeting.meeting_time}`),
        location: meeting.location || '',
        notes: meeting.notes || '',
      });
    } else {
      setEditMode(false);
      setSelectedMeeting(null);
      setFormData({
        meetingDate: dayjs().add(1, 'day'),
        meetingTime: dayjs().hour(22).minute(0),
        location: 'Community Hall',
        notes: '',
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditMode(false);
    setSelectedMeeting(null);
  };

  const handleSubmit = async () => {
    try {
      const payload = {
        meetingDate: formData.meetingDate.format('YYYY-MM-DD'),
        meetingTime: formData.meetingTime.format('HH:mm'),
        location: formData.location,
        notes: formData.notes,
      };

      if (editMode) {
        await axios.put(`/api/meetings/${selectedMeeting.id}`, payload);
        toast.success('Meeting updated successfully');
      } else {
        await axios.post('/api/meetings', payload);
        toast.success('Meeting created successfully');
      }
      
      handleCloseDialog();
      fetchMeetings();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Operation failed');
    }
  };

  const handleDeleteMeeting = async (meeting) => {
    if (window.confirm(`Are you sure you want to delete the meeting scheduled for ${dayjs(meeting.meeting_date).format('MMMM D, YYYY')}? This action cannot be undone and will remove all associated attendance and penalty data.`)) {
      try {
        await axios.delete(`/api/meetings/${meeting.id}`);
        toast.success('Meeting deleted successfully');
        fetchMeetings();
      } catch (error) {
        toast.error(error.response?.data?.error || 'Failed to delete meeting');
      }
    }
  };

  // The click time is only an arrival time while the meeting is happening.
  // Correcting a past meeting afterwards used to record "arrived at" the moment
  // of the correction - e.g. 11 AM for a 10 PM meeting.
  const arrivalStamp = () =>
    selectedMeeting && dayjs(selectedMeeting.meeting_date).isSame(dayjs(), 'day') ? dayjs() : null;

  const handleAttendanceUpdate = async (userId, status, arrivalTime = null) => {
    try {
      await axios.put(`/api/meetings/${selectedMeeting.id}/attendance`, {
        userId,
        attendanceStatus: status,
        arrivalTime: arrivalTime ? arrivalTime.format('HH:mm') : null,
      });
      
      toast.success('Attendance updated successfully');
      fetchMeetingDetails(selectedMeeting.id);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update attendance');
    }
  };

  const handleCompleteMeeting = async (meetingId) => {
    try {
      await axios.put(`/api/meetings/${meetingId}/complete`);
      toast.success('Meeting marked as completed');
      fetchMeetings();
    } catch (error) {
      toast.error('Failed to complete meeting');
    }
  };

  const handleReopenMeeting = async (meeting) => {
    if (window.confirm(`Reopen the meeting held on ${dayjs(meeting.meeting_date).format('MMMM D, YYYY')}? It goes back to Scheduled so attendance can be corrected - mark it completed again when you are done.`)) {
      try {
        await axios.put(`/api/meetings/${meeting.id}/reopen`);
        toast.success('Meeting reopened');
        fetchMeetings();
      } catch (error) {
        toast.error(error.response?.data?.error || 'Failed to reopen meeting');
      }
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'scheduled':
        return 'primary';
      case 'completed':
        return 'success';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const getAttendanceColor = (status) => {
    switch (status) {
      case 'present':
        return 'success';
      case 'late':
        return 'warning';
      case 'absent':
        return 'error';
      default:
        return 'default';
    }
  };

  // Follows the configured meeting day rather than assuming Friday
  const meetingDay = settings?.meetingDay ?? 5;
  const [meetingHour, meetingMinute] = (settings?.meetingTime || '22:00').split(':').map(Number);

  const getNextMeetingDate = () => {
    const now = dayjs();
    const target = now.day() <= meetingDay
      ? now.day(meetingDay)
      : now.add(1, 'week').day(meetingDay);
    return target.hour(meetingHour || 22).minute(meetingMinute || 0).second(0);
  };

  return (
    <Box>
      <PageHeader
        eyebrow="Gatherings"
        accent={ACCENTS.teal}
        title="Meetings"
        subtitle="When the group meets, who attended, and what was agreed."
        actions={isAdmin && (
          <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenDialog()}>
            Schedule Meeting
          </Button>
        )}
      />

      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>Regular schedule:</strong>{' '}
          {settings
            ? `First ${weekdayName(settings.meetingDay)} of every month at ${formatTime(settings.meetingTime)}. Late arrival is subject to a ${formatINR(settings.penaltyAmount)} penalty.`
            : 'Monthly meeting; late arrival is subject to a penalty.'}
        </Typography>
      </Alert>

      {/* Quick Schedule Next Meeting */}
      {isAdmin && (
        <Card sx={{ mb: 3, background: (t) => `linear-gradient(140deg, ${tint(ACCENTS.gold, 0.16)} 0%, ${t.palette.background.paper} 70%)`, border: '1px solid', borderColor: 'divider' }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="h6" >
                  Quick schedule: next meeting
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {getNextMeetingDate().format('MMMM D, YYYY [at] h:mm A')}
                </Typography>
              </Box>
              <Button
                variant="contained"
                color="secondary"
                onClick={() => {
                  setFormData({
                    meetingDate: getNextMeetingDate(),
                    meetingTime: getNextMeetingDate(),
                    location: 'Community Hall',
                    notes: 'Regular monthly meeting',
                  });
                  setDialogOpen(true);
                }}
              >
                Schedule Now
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Meetings Grid */}
      <Grid container spacing={3}>
        {meetings.length === 0 && !loading && (
          <Grid item xs={12}>
            <EmptyState
              icon={<Event />}
              accent={ACCENTS.teal}
              title="No meetings scheduled"
              description="Meetings appear here once scheduled. The group meets on the first Saturday of the month."
            />
          </Grid>
        )}
        {meetings.map((meeting) => (
          <Grid item xs={12} md={6} lg={4} key={meeting.id}>
            <Card 
              sx={{ 
                height: '100%',
                '&:hover': { transform: 'translateY(-4px)', transition: 'transform 0.3s' }
              }}
            >
              <CardContent>
                {/* Status on the left, timing on the right. The mark carries its
                    own label, so nothing here restates it. */}
                <Box display="flex" justifyContent="space-between" alignItems="center" gap={1} mb={2}>
                  <StatusIndicator status={meeting.status} domain="meetings" size="small" />
                  <Chip
                    size="small"
                    variant="outlined"
                    label={dayjs(meeting.meeting_date).isBefore(dayjs(), 'day') ? 'Past' : 'Upcoming'}
                    sx={{ flexShrink: 0 }}
                  />
                </Box>

                <List dense>
                  <ListItem disablePadding>
                    <ListItemIcon>
                      <Event color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Date"
                      secondary={dayjs(meeting.meeting_date).format('MMMM D, YYYY')}
                    />
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemIcon>
                      <AccessTime color="secondary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Time"
                      secondary={dayjs(`2024-01-01 ${meeting.meeting_time}`).format('h:mm A')}
                    />
                  </ListItem>
                  {meeting.location && (
                    <ListItem disablePadding>
                      <ListItemIcon>
                        <LocationOn color="action" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Location"
                        secondary={meeting.location}
                      />
                    </ListItem>
                  )}
                </List>

                {meeting.notes && (
                  <Box sx={{ mt: 2, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                    <Typography variant="body2">
                      {meeting.notes}
                    </Typography>
                  </Box>
                )}

                <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button
                    size="small"
                    startIcon={<People />}
                    onClick={() => fetchMeetingDetails(meeting.id)}
                  >
                    Attendance
                  </Button>
                  {/* Editable at any status: a held meeting still needs its date,
                      place or note corrected. Delete and Complete stay scheduled-only -
                      deleting a held meeting would wipe its attendance and penalties. */}
                  {isAdmin && (
                    <Button
                      size="small"
                      startIcon={<Edit />}
                      onClick={() => handleOpenDialog(meeting)}
                    >
                      Edit
                    </Button>
                  )}
                  {isAdmin && meeting.status === 'completed' && (
                    <Button
                      size="small"
                      color="warning"
                      startIcon={<LockOpen />}
                      onClick={() => handleReopenMeeting(meeting)}
                    >
                      Reopen
                    </Button>
                  )}
                  {isAdmin && meeting.status === 'scheduled' && (
                    <>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteMeeting(meeting)}
                        title="Delete Meeting"
                        color="error"
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                      <Button
                        size="small"
                        color="success"
                        startIcon={<CheckCircle />}
                        onClick={() => handleCompleteMeeting(meeting.id)}
                      >
                        Complete
                      </Button>
                    </>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Add/Edit Meeting Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editMode ? 'Edit Meeting' : 'Schedule New Meeting'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <DatePicker
                label="Meeting Date"
                value={formData.meetingDate}
                onChange={(newValue) => setFormData({ ...formData, meetingDate: newValue })}
                slotProps={{ textField: { fullWidth: true, required: true } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TimePicker
                label="Meeting Time"
                value={formData.meetingTime}
                onChange={(newValue) => setFormData({ ...formData, meetingTime: newValue })}
                slotProps={{ textField: { fullWidth: true, required: true } }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Location"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                placeholder="e.g., Community Hall, Online, etc."
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Meeting agenda, special instructions, etc."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editMode ? 'Update' : 'Schedule'} Meeting
          </Button>
        </DialogActions>
      </Dialog>

      {/* Attendance Dialog */}
      <Dialog 
        open={attendanceDialogOpen} 
        onClose={() => setAttendanceDialogOpen(false)} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>
          Meeting Attendance - {selectedMeeting && dayjs(selectedMeeting.meeting_date).format('MMMM D, YYYY')}
        </DialogTitle>
        <DialogContent>
          {selectedMeeting && (
            <Box>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Time: {dayjs(`2024-01-01 ${selectedMeeting.meeting_time}`).format('h:mm A')} | 
                Location: {selectedMeeting.location || 'Not specified'}
              </Typography>
              
              <Divider sx={{ my: 2 }} />
              
              <List>
                {users.map((user) => {
                  const attendance = attendanceData.find(a => a.user_id === user.id);
                  return (
                    // One line on desktop. On a phone the marks and buttons wrap to their
                    // own full-width row - beside the name they overflowed the dialog.
                    <ListItem key={user.id} sx={{ flexWrap: { xs: 'wrap', sm: 'nowrap' }, rowGap: 1 }}>
                      <ListItemIcon sx={{ minWidth: { xs: 44, sm: 56 } }}>
                        <Avatar sx={{ width: 32, height: 32 }}>
                          {user.full_name.charAt(0)}
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        sx={{ minWidth: 0, flex: { xs: '1 1 0', sm: '1 1 auto' } }}
                        primary={user.full_name}
                        secondary={
                          attendance?.arrival_time
                            ? `Arrived at: ${attendance.arrival_time}`
                            : 'No arrival time recorded'
                        }
                      />
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', width: { xs: '100%', sm: 'auto' } }}>
                        <Box sx={{ flexShrink: 0 }}>
                          <StatusIndicator status={attendance?.attendance_status || 'absent'} size="small" />
                        </Box>
                        {isAdmin && selectedMeeting.status === 'scheduled' && (
                          <Box sx={{ display: 'flex', gap: 0.5, flex: { xs: 1, sm: 'none' } }}>
                            <Button
                              size="small"
                              color="success"
                              sx={{ flex: { xs: 1, sm: 'none' }, minHeight: { xs: 40, sm: 'auto' } }}
                              onClick={() => handleAttendanceUpdate(user.id, 'present', arrivalStamp())}
                            >
                              Present
                            </Button>
                            <Button
                              size="small"
                              color="warning"
                              sx={{ flex: { xs: 1, sm: 'none' }, minHeight: { xs: 40, sm: 'auto' } }}
                              onClick={() => handleAttendanceUpdate(user.id, 'late', arrivalStamp())}
                            >
                              Late
                            </Button>
                            <Button
                              size="small"
                              color="error"
                              sx={{ flex: { xs: 1, sm: 'none' }, minHeight: { xs: 40, sm: 'auto' } }}
                              onClick={() => handleAttendanceUpdate(user.id, 'absent')}
                            >
                              Absent
                            </Button>
                          </Box>
                        )}
                      </Box>
                    </ListItem>
                  );
                })}
              </List>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAttendanceDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Meetings;